import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { buttonVariants } from '@/components/ui/button'
// Completely custom implementation without Radix to avoid body scroll lock
import { Icon, ICONS } from '@/lib/icons'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { apiClient, handleApiError } from '@/services/api'
import type { Player } from '@/types/api'
import { toast } from 'sonner'

interface PlayerSelectorProps {
  value?: string
  onPlayerSelected: (player: Player) => void
  clubId?: string
  /** Single player ID to exclude (deprecated, use excludePlayerIds) */
  excludePlayerId?: string
  /** Array of player IDs to exclude from selection */
  excludePlayerIds?: string[]
  className?: string
}

export type PlayerSelectorHandle = {
  focus: () => void
}

export const PlayerSelector = forwardRef<PlayerSelectorHandle, PlayerSelectorProps>(function PlayerSelector({
  value,
  onPlayerSelected,
  clubId,
  excludePlayerId,
  excludePlayerIds = [],
  className
}: PlayerSelectorProps, ref) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [players, setPlayers] = useState<Player[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [_nextPageToken, setNextPageToken] = useState<string | undefined>()
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null)
  const [clubNames, setClubNames] = useState<Map<string, string>>(new Map())
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasLoadedInitialData = useRef(false)
  
  // Use refs for values that change frequently to avoid recreating loadPlayers
  const clubNamesRef = useRef<Map<string, string>>(new Map())
  const nextPageTokenRef = useRef<string | undefined>(undefined)
  const latestSearchTermRef = useRef<string>('')
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const clubsCatalogCursorRef = useRef<string | undefined>(undefined)
  const clubsCatalogFullyLoadedRef = useRef(false)
  const clubsCatalogFetchPromiseRef = useRef<Promise<void> | null>(null)

  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const showClubAffiliation = !clubId

  const formatPlayerLabel = useCallback((player: Player) => {
    if (!showClubAffiliation) {
      return player.displayName
    }

    const primaryClub = player.clubMemberships?.find(m => m.active) || player.clubMemberships?.[0]
    if (!primaryClub) {
      return player.displayName
    }

    const clubName = clubNames.get(primaryClub.clubId)
    if (clubName) {
      return `${player.displayName} (${clubName})`
    }

    return `${player.displayName} (...)`
  }, [clubNames, showClubAffiliation])

  // Keep refs in sync with state
  useEffect(() => {
    clubNamesRef.current = clubNames
  }, [clubNames])

  useImperativeHandle(ref, () => ({
    focus: () => {
      triggerRef.current?.focus()
    }
  }))

  const fetchClubNamesForIds = useCallback(async (clubIds: Iterable<string>) => {
    if (!showClubAffiliation) {
      return
    }

    const uniqueIds = Array.from(new Set(clubIds))
    if (uniqueIds.length === 0) {
      return
    }

    const getMissingIds = () => uniqueIds.filter((id) => !clubNamesRef.current.has(id))

    let missingIds = getMissingIds()
    if (missingIds.length === 0) {
      return
    }

    if (clubsCatalogFetchPromiseRef.current) {
      await clubsCatalogFetchPromiseRef.current
      missingIds = getMissingIds()
      if (missingIds.length === 0) {
        return
      }
    }

    const fetchPromise = (async () => {
      const pendingIds = new Set(missingIds)
      const updates = new Map<string, string>()

      if (!clubsCatalogFullyLoadedRef.current) {
        try {
          let hasMore = true
          while (pendingIds.size > 0 && hasMore && !clubsCatalogFullyLoadedRef.current) {
            const response = await apiClient.listClubs({
              pageSize: 200,
              cursorAfter: clubsCatalogCursorRef.current
            }, 'player-selector-club-cache')

            response.items.forEach((club) => {
              if (!clubNamesRef.current.has(club.id)) {
                updates.set(club.id, club.name)
              }
              pendingIds.delete(club.id)
            })

            clubsCatalogCursorRef.current = response.endCursor
            hasMore = response.hasNextPage
            if (!response.hasNextPage) {
              clubsCatalogFullyLoadedRef.current = true
            }
          }
        } catch (error) {
          // Fallback to per-club fetching when bulk loading fails
          // eslint-disable-next-line no-console
          console.error('Failed to preload clubs for player selector', error)
        }
      }

      if (pendingIds.size > 0) {
        const unknownLabel = t('common.unknownClub', 'Unknown Club')
        await Promise.allSettled(Array.from(pendingIds).map(async (clubId) => {
          try {
            const club = await apiClient.getClub(clubId)
            updates.set(clubId, club.name)
          } catch (_error) {
            updates.set(clubId, unknownLabel)
          }
        }))
        pendingIds.clear()
      }

      if (updates.size > 0) {
        setClubNames((prev) => {
          const next = new Map(prev)
          updates.forEach((value, key) => {
            next.set(key, value)
          })
          clubNamesRef.current = next
          return next
        })
      }
    })()

    clubsCatalogFetchPromiseRef.current = fetchPromise

    try {
      await fetchPromise
    } finally {
      if (clubsCatalogFetchPromiseRef.current === fetchPromise) {
        clubsCatalogFetchPromiseRef.current = null
      }
    }
  }, [showClubAffiliation, t])

  const loadPlayers = useCallback(async (isLoadMore = false, searchTerm?: string) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      if (searchTerm !== undefined) {
        latestSearchTermRef.current = searchTerm
      }

      const effectiveSearch = searchTerm ?? latestSearchTermRef.current

      // Build excluded IDs list
      const excludedIds = [...excludePlayerIds]
      if (excludePlayerId) {
        excludedIds.push(excludePlayerId)
      }

      const response = await apiClient.listPlayers({
        searchQuery: effectiveSearch ? effectiveSearch : undefined,
        clubId: clubId,
        pageSize: 50,
        cursorAfter: isLoadMore ? nextPageTokenRef.current : undefined,
        excludedIds: excludedIds.length > 0 ? excludedIds : undefined
      }, 'player-selector')

      // Filter to only active players (backend might return inactive in some cases)
      const filteredPlayers = response.items.filter(p => p.active)
      
      if (isLoadMore) {
        setPlayers(prev => [...prev, ...filteredPlayers])
      } else {
        setPlayers(filteredPlayers)
      }
      
      setHasNextPage(response.hasNextPage)
      setNextPageToken(response.endCursor)
      nextPageTokenRef.current = response.endCursor

      const clubIdsToFetch = new Set<string>()
      filteredPlayers.forEach(player => {
        player.clubMemberships?.forEach(membership => {
          if (membership?.clubId) {
            clubIdsToFetch.add(membership.clubId)
          }
        })
      })

      if (clubIdsToFetch.size > 0) {
        await fetchClubNamesForIds(clubIdsToFetch)
      }
    } catch (error: unknown) {
      handleApiError(error, (apiError) => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [clubId, excludePlayerId, excludePlayerIds, fetchClubNamesForIds, t])

  const loadMorePlayers = useCallback(() => {
    if (hasNextPage && !loadingMore) {
      loadPlayers(true, latestSearchTermRef.current)
    }
  }, [hasNextPage, loadingMore, loadPlayers])

  // Add throttling for scroll events
  const throttleRef = useRef<number | null>(null)
  
  // Handle scroll for infinite loading with throttling
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (throttleRef.current) return // Skip if already throttled

    const throttleDuration = isTouchDevice ? 75 : 150
    throttleRef.current = window.setTimeout(() => {
      throttleRef.current = null

      const target = event.target as HTMLDivElement
      const { scrollTop, scrollHeight, clientHeight } = target

      // Trigger load more when scrolled near bottom. Use a tighter threshold on touch devices.
      const threshold = isTouchDevice ? Math.max(clientHeight * 0.2, 60) : 100
      const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold

      // Only trigger if we're near bottom, have more pages, and not already loading
      if (isNearBottom && hasNextPage && !loadingMore && !loading) {
        loadMorePlayers()
      }
    }, throttleDuration) // Throttle to reduce event spam
  }, [hasNextPage, loadingMore, loading, loadMorePlayers, isTouchDevice])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const touchCapable =
        'ontouchstart' in window || (typeof window.navigator !== 'undefined' && window.navigator.maxTouchPoints > 0)
      setIsTouchDevice(touchCapable)
    }

    return () => {
      if (throttleRef.current) {
        window.clearTimeout(throttleRef.current)
        throttleRef.current = null
      }
    }
  }, [])

  // Handle dropdown open and search separately to avoid multiple API calls
  useEffect(() => {
    // When dropdown opens, load initial data only if we don't have any
    if (open && players.length === 0 && !loading && searchQuery === '') {
      hasLoadedInitialData.current = true
      loadPlayers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, searchQuery])

  useEffect(() => {
    // When user types in search (after debounce), reload from scratch
    if (debouncedSearchQuery && hasLoadedInitialData.current) {
      // Reset pagination
      setNextPageToken(undefined)
      nextPageTokenRef.current = undefined
      setHasNextPage(false)
      latestSearchTermRef.current = debouncedSearchQuery
      loadPlayers(false, debouncedSearchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery])

  // Track if we've just cleared search to avoid loop
  const previousSearchQuery = useRef(searchQuery)
  useEffect(() => {
    // When user clears search, reload with no filter (only trigger on transition to empty)
    if (searchQuery === '' && previousSearchQuery.current !== '' && hasLoadedInitialData.current) {
      // Reset pagination
      setNextPageToken(undefined)
      nextPageTokenRef.current = undefined
      setHasNextPage(false)
      latestSearchTermRef.current = ''
      loadPlayers(false, '')
    }
    previousSearchQuery.current = searchQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  useEffect(() => {
    // When excluded IDs change and dropdown is open, reload the list
    // We join the array to create a stable string for comparison
    if (open && hasLoadedInitialData.current) {
      // Reset pagination
      setNextPageToken(undefined)
      nextPageTokenRef.current = undefined
      setHasNextPage(false)
      loadPlayers(false, latestSearchTermRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludePlayerIds.join(','), excludePlayerId, open])

  useEffect(() => {
    if (!value) {
      if (selectedPlayer) {
        setSelectedPlayer(null)
      }
      return
    }

    if (selectedPlayer?.id === value) {
      return
    }

    const player = players.find(p => p.id === value)
    if (player) {
      setSelectedPlayer(player)
    }
  }, [value, players, selectedPlayer])

  const handlePlayerSelect = (player: Player) => {
    setSelectedPlayer(player)
    setOpen(false)
    onPlayerSelected(player)
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(buttonVariants({ variant: 'outline' }), 'w-full justify-between text-left', className)}
      >
        {selectedPlayer ? (
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{formatPlayerLabel(selectedPlayer)}</div>
          </div>
        ) : (
          <span className="text-muted-foreground">{t('players.selectPlayer')}</span>
        )}
        <Icon icon={ICONS.swap} width={16} height={16} className="ml-2 opacity-50 flex-shrink-0" />
      </button>

      {/* Native Dropdown - No Radix, No Body Scroll Lock */}
      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover text-popover-foreground rounded-md border shadow-md">
            <div className="flex flex-col">
              {/* Search Input */}
              <div className="flex items-center border-b px-3">
                <svg
                  className="mr-2 h-4 w-4 shrink-0 opacity-50"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  autoFocus
                  className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t('common.search') + '...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Scrollable Content - Now truly native scrolling */}
              <div 
                ref={scrollContainerRef}
                className="max-h-60 overflow-y-auto overscroll-contain" 
                onScroll={handleScroll}
                style={{ 
                  scrollbarGutter: 'stable',
                  // Force scrolling behavior
                  touchAction: 'pan-y',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {loading && players.length === 0 && (
                  <div className="py-4 text-center">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
                
                {!loading && players.length === 0 && (
                  <div className="py-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('players.noPlayers')}
                    </p>
                  </div>
                )}
                
                {players.map((player) => {
                  const label = formatPlayerLabel(player)

                  return (
                    <div
                      key={player.id}
                      onClick={() => handlePlayerSelect(player)}
                      className="flex items-center px-2 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm"
                    >
                      <Icon
                        icon={ICONS.tickCircle}
                        width={16}
                        height={16}
                        className={cn(
                          "mr-2 flex-shrink-0",
                          selectedPlayer?.id === player.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{label}</div>
                      </div>
                    </div>
                  )
                })}
                
                {hasNextPage && (
                  <div className="border-t border-border bg-background">
                    <button
                      onClick={loadMorePlayers}
                      disabled={loadingMore}
                      className="w-full py-3 px-4 text-sm font-medium text-primary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loadingMore ? (
                        <>
                          <LoadingSpinner size="sm" />
                          {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <span>{t('common.loadMore', 'Load more...')}</span>
                          <span className="text-xs opacity-70">⬇</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
})
